import { create } from 'zustand';

/**
 * Default fallback address configuration (used for standard / unconfigured countries)
 */
export const DEFAULT_ADDRESS_CONFIG = {
  street_1: {
    label: 'Street 1',
    display_label: 'Street 1',
    placeholder: 'Street address line 1',
    requiredMessage: 'Street 1 is required',
  },
  street_2: {
    label: 'Street 2',
    display_label: 'Street 2',
    placeholder: 'Apartment, suite, unit, building, floor, etc. (Optional)',
    requiredMessage: '',
  },
  city: {
    label: 'City',
    display_label: 'City',
    placeholder: 'City',
    requiredMessage: 'City is required',
  },
  state: {
    label: 'State / Province',
    display_label: 'State / Province',
    placeholder: 'Select State',
    requiredMessage: 'State / Province is required',
  },
  pin_code: {
    label: 'ZIP / Postal Code',
    display_label: 'ZIP / Postal Code',
    placeholder: 'Postal / ZIP code',
    requiredMessage: 'ZIP / Postal Code is required',
  },
};

/**
 * Built-in Country-Specific Address Configurations
 * Easily extensible for GCC and international jurisdictions.
 */
export const DEFAULT_COUNTRY_REGISTRY = {
  // Sultanate of Oman - Royal Decree No. 27/2021 (Unified National Address)
  OM: {
    country_name: 'Oman',
    street_1: {
      label: 'Way / Street Number',
      arabic_label: 'رقم السكة / الشارع',
      display_label: 'Way / Street Number (رقم السكة)',
      placeholder: 'Way Number or Street Name',
      requiredMessage: 'Way / Street Number is required',
    },
    street_2: {
      label: 'Building / Block Number',
      arabic_label: 'رقم المبنى / المجمع',
      display_label: 'Building / Block Number (رقم المبنى)',
      placeholder: 'Building No., Flat or Block (Optional)',
      requiredMessage: '',
    },
    city: {
      label: 'Wilayat',
      arabic_label: 'الولاية',
      display_label: 'Wilayat (الولاية)',
      placeholder: 'Wilayat (e.g. Bawshar, Seeb, Salalah)',
      requiredMessage: 'Wilayat is required',
    },
    state: {
      label: 'Governorate',
      arabic_label: 'المحافظة',
      display_label: 'Governorate (المحافظة)',
      placeholder: 'Select Governorate',
      requiredMessage: 'Governorate is required',
    },
    pin_code: {
      label: 'Postal Code',
      arabic_label: 'الرمز البريدي',
      display_label: 'Postal Code (الرمز البريدي)',
      placeholder: 'Postal code',
      requiredMessage: 'Postal Code is required',
    },
  },

  // Kingdom of Bahrain - National Address System (Road, Building, Block)
  BH: {
    country_name: 'Bahrain',
    street_1: {
      label: 'Road',
      arabic_label: 'طريق',
      display_label: 'Road (طريق)',
      placeholder: 'Road number / name',
      requiredMessage: 'Road is required',
    },
    street_2: {
      label: 'Building',
      arabic_label: 'مبنى',
      display_label: 'Building (مبنى)',
      placeholder: 'Building / Flat number (Optional)',
      requiredMessage: '',
    },
    city: {
      label: 'Block',
      arabic_label: 'مجمع',
      display_label: 'Block (مجمع)',
      placeholder: 'Block number (e.g. 338)',
      requiredMessage: 'Block is required',
    },
    state: {
      label: 'Governorate',
      arabic_label: 'محافظة',
      display_label: 'Governorate (محافظة)',
      placeholder: 'Select Governorate',
      requiredMessage: 'Governorate is required',
    },
    pin_code: {
      label: 'Postal Code',
      arabic_label: 'الرمز البريدي',
      display_label: 'Postal Code (الرمز البريدي)',
      placeholder: 'Postal code',
      requiredMessage: 'Postal Code is required',
    },
  },

  // Kingdom of Saudi Arabia - National Address (SPL)
  SA: {
    country_name: 'Saudi Arabia',
    street_1: {
      label: 'Street Name',
      arabic_label: 'اسم الشارع',
      display_label: 'Street Name (اسم الشارع)',
      placeholder: 'Street Name',
      requiredMessage: 'Street Name is required',
    },
    street_2: {
      label: 'Building & Secondary No.',
      arabic_label: 'رقم المبنى والرقم الإضافي',
      display_label: 'Building & Secondary No. (رقم المبنى)',
      placeholder: 'Building No. & 4-digit Secondary No. (Optional)',
      requiredMessage: '',
    },
    city: {
      label: 'District / City',
      arabic_label: 'الحي / المدينة',
      display_label: 'District / City (الحي / المدينة)',
      placeholder: 'District or City',
      requiredMessage: 'District / City is required',
    },
    state: {
      label: 'Region / Province',
      arabic_label: 'المنطقة',
      display_label: 'Region / Province (المنطقة)',
      placeholder: 'Select Region',
      requiredMessage: 'Region is required',
    },
    pin_code: {
      label: 'Postal Code',
      arabic_label: 'الرمز البريدي',
      display_label: 'Postal Code (الرمز البريدي)',
      placeholder: '5-digit Postal Code',
      requiredMessage: 'Postal Code is required',
    },
  },

  // United Arab Emirates (UAE)
  AE: {
    country_name: 'United Arab Emirates',
    street_1: {
      label: 'Street / Area',
      display_label: 'Street / Area',
      placeholder: 'Street or Area name',
      requiredMessage: 'Street / Area is required',
    },
    street_2: {
      label: 'Building / Villa No.',
      display_label: 'Building / Villa No.',
      placeholder: 'Building, Villa, or Flat number (Optional)',
      requiredMessage: '',
    },
    city: {
      label: 'City',
      display_label: 'City',
      placeholder: 'City',
      requiredMessage: 'City is required',
    },
    state: {
      label: 'Emirate',
      display_label: 'Emirate',
      placeholder: 'Select Emirate',
      requiredMessage: 'Emirate is required',
    },
    pin_code: {
      label: 'Makani / PO Box',
      display_label: 'Makani / PO Box',
      placeholder: '10-digit Makani or PO Box',
      requiredMessage: 'Makani / PO Box is required',
    },
  },

  // State of Qatar
  QA: {
    country_name: 'Qatar',
    street_1: {
      label: 'Street Number',
      arabic_label: 'رقم الشارع',
      display_label: 'Street Number (رقم الشارع)',
      placeholder: 'Street number / name',
      requiredMessage: 'Street Number is required',
    },
    street_2: {
      label: 'Building Number',
      arabic_label: 'رقم المبنى',
      display_label: 'Building Number (رقم المبنى)',
      placeholder: 'Building number (Optional)',
      requiredMessage: '',
    },
    city: {
      label: 'Zone',
      arabic_label: 'المنطقة',
      display_label: 'Zone (المنطقة)',
      placeholder: 'Zone number (e.g. Zone 55)',
      requiredMessage: 'Zone is required',
    },
    state: {
      label: 'Municipality',
      arabic_label: 'البلدية',
      display_label: 'Municipality (البلدية)',
      placeholder: 'Select Municipality',
      requiredMessage: 'Municipality is required',
    },
    pin_code: {
      label: 'Postal Code',
      arabic_label: 'الرمز البريدي',
      display_label: 'Postal Code (الرمز البريدي)',
      placeholder: 'Postal code',
      requiredMessage: 'Postal Code is required',
    },
  },

  // State of Kuwait - PACI Unified Addressing
  KW: {
    country_name: 'Kuwait',
    street_1: {
      label: 'Street / Avenue',
      arabic_label: 'الشارع / الجادة',
      display_label: 'Street / Avenue (الشارع)',
      placeholder: 'Street or Avenue',
      requiredMessage: 'Street is required',
    },
    street_2: {
      label: 'Building / PACI No.',
      arabic_label: 'رقم المبنى / الرقم الآلي',
      display_label: 'Building / PACI No. (رقم المبنى)',
      placeholder: 'Building number or Civil ID / PACI (Optional)',
      requiredMessage: '',
    },
    city: {
      label: 'Block / Area',
      arabic_label: 'القطعة / المنطقة',
      display_label: 'Block / Area (القطعة)',
      placeholder: 'Block number or Area',
      requiredMessage: 'Block is required',
    },
    state: {
      label: 'Governorate',
      arabic_label: 'المحافظة',
      display_label: 'Governorate (المحافظة)',
      placeholder: 'Select Governorate',
      requiredMessage: 'Governorate is required',
    },
    pin_code: {
      label: 'Postal Code',
      arabic_label: 'الرمز البريدي',
      display_label: 'Postal Code (الرمز البريدي)',
      placeholder: 'Postal code',
      requiredMessage: 'Postal Code is required',
    },
  },
};

// Aliases for name or common abbreviation matching
const COUNTRY_KEY_ALIASES = {
  OMAN: 'OM',
  OMN: 'OM',
  '58': 'OM',
  '+968': 'OM',
  '968': 'OM',

  BAHRAIN: 'BH',
  BHR: 'BH',
  '126': 'BH',
  '+973': 'BH',
  '973': 'BH',

  'SAUDI ARABIA': 'SA',
  KSA: 'SA',
  SAU: 'SA',
  '221': 'SA',
  '+966': 'SA',
  '966': 'SA',

  'UNITED ARAB EMIRATES': 'AE',
  UAE: 'AE',
  ARE: 'AE',
  '76': 'AE',
  '+971': 'AE',
  '971': 'AE',

  QATAR: 'QA',
  QAT: 'QA',
  '93': 'QA',
  '+974': 'QA',
  '974': 'QA',

  KUWAIT: 'KW',
  KWT: 'KW',
  '184': 'KW',
  '+965': 'KW',
  '965': 'KW',
};

/**
 * Resolves any country identifier (ISO code, name, ID, or country object) to an uppercase key.
 */
export const normalizeCountryKey = (countryInput, countryList = []) => {
  if (!countryInput) return null;

  // If already an object (e.g. { id: 1, name: 'Oman', country_code: 'OM' })
  if (typeof countryInput === 'object') {
    const code = countryInput.iso_code || countryInput.country_code || countryInput.code;
    if (code) {
      const normalizedCode = String(code).trim().toUpperCase();
      if (DEFAULT_COUNTRY_REGISTRY[normalizedCode] || COUNTRY_KEY_ALIASES[normalizedCode]) {
        return COUNTRY_KEY_ALIASES[normalizedCode] || normalizedCode;
      }
    }
    const name = countryInput.name || countryInput.country_name || countryInput.label;
    if (name) {
      const normalizedName = String(name).trim().toUpperCase();
      if (COUNTRY_KEY_ALIASES[normalizedName]) {
        return COUNTRY_KEY_ALIASES[normalizedName];
      }
      if (DEFAULT_COUNTRY_REGISTRY[normalizedName]) {
        return normalizedName;
      }
    }
    // If object only has id, fall through to id lookup
    if (countryInput.id || countryInput.value) {
      countryInput = countryInput.id || countryInput.value;
    }
  }

  const str = String(countryInput).trim();
  const upper = str.toUpperCase();

  // Direct ISO match
  if (DEFAULT_COUNTRY_REGISTRY[upper]) {
    return upper;
  }

  // Alias match (e.g. 'OMAN' -> 'OM', 'BAHRAIN' -> 'BH')
  if (COUNTRY_KEY_ALIASES[upper]) {
    return COUNTRY_KEY_ALIASES[upper];
  }

  // Lookup in passed countryList if countryInput was an ID (number or numeric string)
  if (Array.isArray(countryList) && countryList.length > 0) {
    const matched = countryList.find(
      (c) =>
        String(c.id) === str ||
        String(c.value) === str ||
        String(c.country_code || '').toUpperCase() === upper ||
        String(c.name || c.label || '').toUpperCase() === upper
    );
    if (matched) {
      const code = (matched.iso_code || matched.country_code || matched.code || '').toUpperCase();
      if (DEFAULT_COUNTRY_REGISTRY[code] || COUNTRY_KEY_ALIASES[code]) {
        return COUNTRY_KEY_ALIASES[code] || code;
      }
      const name = (matched.name || matched.label || '').toUpperCase();
      if (COUNTRY_KEY_ALIASES[name]) {
        return COUNTRY_KEY_ALIASES[name];
      }
    }
  }

  return null;
};

/**
 * Zustand Store for dynamic Country Address Field Configurations
 */
const useAddressConfigStore = create((set, get) => ({
  customRegistry: {},

  /**
   * Register or override a country configuration dynamically at runtime.
   */
  registerCountryConfig: (countryKey, config) => {
    const key = String(countryKey).trim().toUpperCase();
    set((state) => ({
      customRegistry: {
        ...state.customRegistry,
        [key]: config,
      },
    }));
  },

  /**
   * Reset any runtime custom configurations back to defaults.
   */
  resetAddressConfig: () => {
    set({ customRegistry: {} });
  },

  /**
   * Retrieve the complete address configuration for a given country.
   * If the country is not configured or invalid, safely falls back to standard labels.
   */
  getConfig: (countryInput, countryList = []) => {
    const state = get();
    const resolvedKey = normalizeCountryKey(countryInput, countryList);

    const mergedRegistry = {
      ...DEFAULT_COUNTRY_REGISTRY,
      ...state.customRegistry,
    };

    if (resolvedKey && mergedRegistry[resolvedKey]) {
      const countryConfig = mergedRegistry[resolvedKey];
      return {
        countryKey: resolvedKey,
        isConfigured: true,
        street_1: { ...DEFAULT_ADDRESS_CONFIG.street_1, ...countryConfig.street_1 },
        street_2: { ...DEFAULT_ADDRESS_CONFIG.street_2, ...countryConfig.street_2 },
        city: { ...DEFAULT_ADDRESS_CONFIG.city, ...countryConfig.city },
        state: { ...DEFAULT_ADDRESS_CONFIG.state, ...countryConfig.state },
        pin_code: { ...DEFAULT_ADDRESS_CONFIG.pin_code, ...countryConfig.pin_code },
      };
    }

    return {
      countryKey: null,
      isConfigured: false,
      ...DEFAULT_ADDRESS_CONFIG,
    };
  },

  /**
   * Helper to quickly get a field's display label.
   */
  getFieldLabel: (fieldName, countryInput, countryList = [], t = null) => {
    const cfg = get().getConfig(countryInput, countryList);
    const fieldCfg = cfg[fieldName] || DEFAULT_ADDRESS_CONFIG[fieldName] || {};
    const rawLabel = fieldCfg.display_label || fieldCfg.label || fieldName;

    // If a translation function is provided and the country is standard/fallback, allow translation
    if (typeof t === 'function' && !cfg.isConfigured) {
      return t(rawLabel);
    }
    return rawLabel;
  },

  /**
   * Helper to quickly get a field's placeholder.
   */
  getFieldPlaceholder: (fieldName, countryInput, countryList = [], t = null) => {
    const cfg = get().getConfig(countryInput, countryList);
    const fieldCfg = cfg[fieldName] || DEFAULT_ADDRESS_CONFIG[fieldName] || {};
    const rawPlaceholder = fieldCfg.placeholder || '';

    if (typeof t === 'function' && !cfg.isConfigured && rawPlaceholder) {
      return t(rawPlaceholder);
    }
    return rawPlaceholder;
  },
}));

/**
 * Custom React Hook for components to dynamically consume address configuration.
 */
export const useAddressConfig = (countryInput, countryList = []) => {
  const store = useAddressConfigStore();
  const config = store.getConfig(countryInput, countryList);

  const getLabel = (fieldName, t = null) =>
    store.getFieldLabel(fieldName, countryInput, countryList, t);

  const getPlaceholder = (fieldName, t = null) =>
    store.getFieldPlaceholder(fieldName, countryInput, countryList, t);

  return {
    ...config,
    getLabel,
    getPlaceholder,
  };
};

/**
 * Maps address field error keys (billing_city, city, etc.) to dynamic regional labels
 * so ErrorNotification renders 'Billing Wilayat', 'Billing Governorate', etc.
 * without modifying ErrorNotification component itself or the inline input errors.
 */
export const mapAddressErrors = (errors, billConfig, shipConfig) => {
  if (!errors || typeof errors !== 'object') return errors;

  const getCleanKey = (cfg, fieldKey, prefix = '') => {
    const configKey = (fieldKey === 'zipcode' || fieldKey === 'pin_code') ? 'pin_code' : fieldKey;
    const fieldCfg = cfg[configKey];
    if (!fieldCfg) return null;
    let label = fieldCfg.short_label || fieldCfg.label || fieldKey;
    label = label
      .replace(/\s*\/\s*/g, '_')
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_]/g, '')
      .trim();
    return `${prefix}${label}`;
  };

  const mapped = {};
  for (const [key, msg] of Object.entries(errors)) {
    if (!msg) continue;

    // Billing fields (billing_city, billing_state, etc.)
    if (key.startsWith('billing_') && billConfig?.isConfigured) {
      const sub = key.replace('billing_', '');
      const newKey = getCleanKey(billConfig, sub, 'billing_');
      if (newKey) {
        mapped[newKey] = msg;
        continue;
      }
    }

    // Shipping fields (shipping_city, shipping_state, etc.)
    if (key.startsWith('shipping_') && shipConfig?.isConfigured) {
      const sub = key.replace('shipping_', '');
      const newKey = getCleanKey(shipConfig, sub, 'shipping_');
      if (newKey) {
        mapped[newKey] = msg;
        continue;
      }
    }

    // Standalone popup fields (city, state, zipcode, street_1)
    if (billConfig?.isConfigured) {
      const newKey = getCleanKey(billConfig, key, '');
      if (newKey) {
        mapped[newKey] = msg;
        continue;
      }
    }

    mapped[key] = msg;
  }
  return mapped;
};

export default useAddressConfigStore;
